import { useBookingModuleStoreActions } from "PFApp/booking/store";
import { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { PermissionRule } from "PFTypes";
import { RefObject, useCallback, useRef } from "react";
import { useLatest } from "react-use";

import { BarDataType } from "../../bars/bars_data_attributes";
import { DnDMode } from "../../bars/booking_bar/use_booking_bar_dnd";
import { CalendarEventsManager } from "../calendar_event_handler.types";
import { getDataAttributes } from "../get_data_attributes";
import { DRAG_HOLD_DELAY_MS } from "./booking_drag_and_drop_manager.constants";
import { PointerDownData } from "./booking_drag_and_drop_manager.types";
import { getPointerMoveAction, PointerMoveAction } from "./get_pointer_move_action";
import { DocumentPointerHandlers, useDocumentPointerListeners } from "./use_document_pointer_listeners";
import { useDragSession } from "./use_drag_session";
import { useHoldTimer } from "./use_hold_timer";
import { useIsBookingDraggable } from "./use_is_booking_draggable";

const getDraggingMode = (dataType: string, canDragVertically: boolean): DnDMode => {
  if (dataType === BarDataType.EdgeStart) {
    return DnDMode.StartEdge;
  }
  if (dataType === BarDataType.EdgeEnd) {
    return DnDMode.EndEdge;
  }
  return canDragVertically ? DnDMode.HorizontalVertical : DnDMode.Horizontal;
};

export const useBookingDragAndDropManager = (
  dropZoneRef: RefObject<HTMLDivElement | null>
): CalendarEventsManager => {
  const { resetBookingDragAndDropData, getBookingDragAndDropData, getCurrentRowsByKey } =
    useBookingModuleStoreActions();
  const { checkIsBookingDraggable } = useIsBookingDraggable();
  const dragSession = useDragSession({ dropZoneRef });
  const { startListening, stopListening } = useDocumentPointerListeners();

  const isPermittedTo = useIsCurrentUserPermittedTo();
  const canDragVertically =
    isPermittedTo(PermissionRule.ReassignBookingsAllResources) &&
    isPermittedTo(PermissionRule.BookingResourcer);

  const pointerDownDataRef = useRef<PointerDownData | null>(null);
  const { startHoldTimer, clearHoldTimer } = useHoldTimer(DRAG_HOLD_DELAY_MS);

  const startDrag = useCallback(() => {
    clearHoldTimer();
    if (pointerDownDataRef.current) {
      dragSession.startDrag(pointerDownDataRef.current);
    }
  }, [clearHoldTimer, dragSession]);

  const cancelDrag = useCallback(() => {
    clearHoldTimer();
    stopListening();
    pointerDownDataRef.current = null;
    dragSession.cancelDrag();
  }, [clearHoldTimer, dragSession, stopListening]);

  const onPointerMove = useCallback(
    (event: PointerEvent) => {
      const action = getPointerMoveAction({
        pointerDownData: pointerDownDataRef.current,
        isDragging: getBookingDragAndDropData().draggingMode !== null,
        event
      });
      const handlers: Record<PointerMoveAction, () => void> = {
        [PointerMoveAction.Ignore]: () => undefined,
        [PointerMoveAction.Start]: startDrag,
        [PointerMoveAction.Move]: () => dragSession.moveDrag(event),
        [PointerMoveAction.Cancel]: cancelDrag
      };
      handlers[action]();
    },
    [cancelDrag, dragSession, getBookingDragAndDropData, startDrag]
  );

  const onPointerUp = useCallback(() => {
    clearHoldTimer();
    stopListening();
    pointerDownDataRef.current = null;
    dragSession.dropDrag();
  }, [clearHoldTimer, dragSession, stopListening]);

  const documentHandlersRef = useLatest<DocumentPointerHandlers>({
    onPointerMove,
    onPointerUp,
    onPointerCancel: cancelDrag
  });

  const onPointerDown = useCallback(
    (event: PointerEvent) => {
      if (event.button !== 0 || !event.isPrimary) {
        return;
      }

      clearHoldTimer();

      if (getBookingDragAndDropData().isBookingUpdating) {
        pointerDownDataRef.current = null;
        return;
      }
      resetBookingDragAndDropData();

      const closestElement = (event.target as Element | null)?.closest("[data-type]") ?? null;
      const dataType = closestElement?.getAttribute("data-type") ?? "";

      if (
        ![BarDataType.Booking, BarDataType.EdgeStart, BarDataType.EdgeEnd].includes(dataType as BarDataType)
      ) {
        pointerDownDataRef.current = null;
        return;
      }

      const { entityId } = getDataAttributes(closestElement);
      if (!entityId || !checkIsBookingDraggable(Number(entityId))) {
        pointerDownDataRef.current = null;
        return;
      }

      const bookingBarElement = (event.target as Element).closest<HTMLElement>(
        `[data-type="${BarDataType.Booking}"]`
      );
      const sourceRowKey =
        (event.target as Element)
          .closest<HTMLElement>(`[data-type="${BarDataType.Row}"]`)
          ?.getAttribute("data-row-key") ?? null;

      if (!bookingBarElement || !sourceRowKey) {
        pointerDownDataRef.current = null;
        return;
      }

      const sourceRow = getCurrentRowsByKey()[sourceRowKey] ?? null;
      if (!sourceRow) {
        pointerDownDataRef.current = null;
        return;
      }

      const draggingMode = getDraggingMode(dataType, canDragVertically);

      pointerDownDataRef.current = {
        bookingId: Number(entityId),
        originX: event.clientX,
        originY: event.clientY,
        draggingMode,
        sourceRow
      };

      startListening(documentHandlersRef, event.pointerId);
      startHoldTimer(startDrag);
    },
    [
      canDragVertically,
      checkIsBookingDraggable,
      clearHoldTimer,
      getBookingDragAndDropData,
      getCurrentRowsByKey,
      resetBookingDragAndDropData,
      startDrag,
      startHoldTimer,
      startListening,
      documentHandlersRef
    ]
  );

  return { onPointerDown };
};
